import * as React from 'react';
import { useDispatch } from 'react-redux';
import * as _ from 'lodash';
import {
  BulkUploadSummary,
  formatHumanFriendlyElapsedTime,
  formatDate,
  formatTime,
  Logging,
  formatDecimal,
  Notification,
  NotificationData,
  ResponseError,
  API,
  BulkUploadSummaryCollection,
  stringToDate,
  BulkUploadStatus,
  ContentLoading,
  utcToLocal,
  VertexLink,
  formatHumanFriendlyDate,
} from '@vcd-ecm-ui/Common';
import { VtxPagination, VtxIcon } from '@vertexinc/vtx-ui-react-component-library';
import BulkUploadHistoryProps from './BulkUploadHistoryProps.interface';
import '../bulk-upload.scss';
import './bulk-upload-history.scss';

const BulkUploadHistoryRefreshIntervalSeconds = 30;

const BulkUploadHistory = ({ gatewayEndPoint, numberUploads }: BulkUploadHistoryProps): JSX.Element => {
  const [historyList, setHistoryList] = React.useState(null);
  const [notification, setNotification] = React.useState(null);
  const [totalBulkUploadHistory, setTotalBulkUploadHistory] = React.useState(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [currentPageSize, setCurrentPageSize] = React.useState(25);
  const dispatch = useDispatch();

  const onNotificationClose = (): void => {
    setNotification(null);
  };

  const renderTransferringInProgressItem = (status: string, historyItem: BulkUploadSummary): JSX.Element => {
    const lastUpdate = `for the last ${formatHumanFriendlyElapsedTime(stringToDate(historyItem.createDateTime))}`;

    return (
      <div className="buhHistoryItemInProgress">
        <VtxIcon color="gray" fontSize={32} type="cloud-upload" className="buhHistoryItemInProgressIcon" />
        <div className="buhHistoryItemInProgressBold"><span>{status}</span></div>&nbsp;<span>{lastUpdate}</span>
      </div>
    );
  };

  const renderVirusScanningItem = (status: string, historyItem: BulkUploadSummary): JSX.Element => {
    const lastUpdate = `for the last ${formatHumanFriendlyElapsedTime(stringToDate(historyItem.lastUpdateDateTime))}`;

    return (
      <div className="buhHistoryItemInProgress">
        <VtxIcon color="gray" fontSize={32} type="bug" className="buhHistoryItemInProgressIcon" />
        <div className="buhHistoryItemInProgressBold"><span>{status}</span></div>&nbsp;<span>{lastUpdate}</span>
      </div>
    );
  };

  const renderQueuedItem = (status: string, historyItem: BulkUploadSummary): JSX.Element => {
    const lastUpdate = formatHumanFriendlyDate(stringToDate(historyItem.lastUpdateDateTime));

    return (
      <div className="buhHistoryItemInProgress">
        <VtxIcon color="gray" fontSize={32} type="hourglass" className="buhHistoryItemInProgressIcon" />
        <div className="buhHistoryItemInProgressBold"><span>{status}</span></div>&nbsp;<span>{lastUpdate}</span>
      </div>
    );
  };

  const renderProcessingInProgressItem = (status: string, historyItem: BulkUploadSummary): JSX.Element => {
    const lastUpdate = `for the last ${formatHumanFriendlyElapsedTime(stringToDate(historyItem.startDateTime))}`;

    return (
      <div className="buhHistoryItemInProgress">
        <img src="/images/loading-animation.svg" alt="loading" className="buhHistoryItemInProgressImage" />
        <div className="buhHistoryItemInProgressBold"><span>{status}</span></div>&nbsp;<span>{lastUpdate}</span>
      </div>
    );
  };

  const renderBusyItem = (historyItem: BulkUploadSummary): JSX.Element => {
    switch (historyItem.status) {
      case BulkUploadStatus.TRANSFERRING:
        return renderTransferringInProgressItem('Transferring', historyItem);
      case BulkUploadStatus.VIRUS_SCAN_QUEUED:
        return renderQueuedItem('Queued for Virus Scanning', historyItem);
      case BulkUploadStatus.SCANNING:
        return renderVirusScanningItem('Virus Scanning', historyItem);
      case BulkUploadStatus.QUEUED:
        return renderQueuedItem('Queued for Processing', historyItem);
      case BulkUploadStatus.IN_PROGRESS:
        return renderProcessingInProgressItem('Processing', historyItem);
      default:
        return null;
    }
  };

  const renderStatusItem = (status: BulkUploadStatus, count: number, text: string): JSX.Element => {
    let statusIcon: any = 'info-circle'; // eslint-disable-line @typescript-eslint/no-explicit-any
    let statusIconColor: any = 'gray'; // eslint-disable-line @typescript-eslint/no-explicit-any
    let statusClass = 'buhStatusInfo';

    if (status === BulkUploadStatus.SUCCESS) {
      if (count > 0) {
        statusClass = 'buhStatusSuccess';
        statusIcon = 'check-circle';
        statusIconColor = 'green';
      }
    } else if (status === BulkUploadStatus.FAILED) {
      if (count > 0) {
        statusClass = 'buhStatusError';
        statusIcon = 'exclamation-circle';
        statusIconColor = 'red';
      }
    }

    return (
      <div className="buhHistoryItemStatusContainer">
        <div className="buhHistoryItemStatusImage">
          <VtxIcon type={statusIcon} fontSize={22} color={statusIconColor} />
        </div>
        <div className={`buhHistoryItemStatus ${statusClass}`}>
          <div className="buhHistoryItemStatusCount">
            {formatDecimal(count)}
          </div>
          <div className="buhHistoryItemStatusText">
            {text}
          </div>
        </div>
      </div>
    );
  };

  const renderCompleteItem = (historyItem: BulkUploadSummary): JSX.Element => {
    const successCount: number = historyItem.succeededRecords;
    const errorCount: number = historyItem.failedRecords;
    const hasErrorFile: boolean = (historyItem.errorFile !== null) && (historyItem.errorFile !== '');
    const expiredErrorFile: boolean = (historyItem.status === BulkUploadStatus.FAILED) && !hasErrorFile;
    const hasErrorText: boolean = (historyItem.status === BulkUploadStatus.FAILED) && historyItem.reason !== null;
    const lastUpdate = formatHumanFriendlyDate(stringToDate(historyItem.endDateTime));

    return (
      <div className="buhHistoryItemComplete">
        {
          hasErrorText
            ? (
              <>
                <div className="buhHistoryItemFailed">
                  <VtxIcon type="exclamation-circle" fontSize={22} color="red" className="buhHistoryItemFailedImage" />
                  <div className="buhHistoryItemInProgressBold"><span>Failed</span></div>&nbsp;<span>{lastUpdate}</span>
                </div>
                <div className="buhHistoryItemErrorText">
                  {historyItem.reason}
                </div>
              </>
            )
            : (
              <>
                {renderStatusItem(BulkUploadStatus.SUCCESS, successCount, 'Successful')}
                {renderStatusItem(BulkUploadStatus.FAILED, errorCount, 'Errors')}
                <div className="buhHistoryItemErrorFile">
                  {
                    hasErrorFile
                      ? (
                        <a href={historyItem.errorFile}>Download Error File</a>
                      )
                      : expiredErrorFile
                        ? (
                          <>
                            Error file expired
                          </>
                        )
                        : (
                          <>
                            No Errors
                          </>
                        )
                  }
                </div>
              </>
            )
        }
      </div>
    );
  };

  // Render the History
  const renderHistory = (): JSX.Element[] | JSX.Element => {
    if (historyList) {
      Logging.Debug('Render Bulk Upload History:', historyList.length);

      if (historyList.length) {
        // Loop through the History
        return historyList.map((historyItem: BulkUploadSummary) => {
          Logging.Debug('History Item:', historyItem);

          const date = formatDate(utcToLocal(stringToDate(historyItem.createDateTime)));
          const time = formatTime(utcToLocal(stringToDate(historyItem.createDateTime)));
          const createdAtBy = `${date} at ${time}`;
          const isComplete = (historyItem.status === BulkUploadStatus.SUCCESS) || (historyItem.status === BulkUploadStatus.FAILED);

          // Generate the HTML for the History
          return (
            <div className="buhHistoryItem" key={historyItem.uuid}>
              <div className="buhHistoryItemLeft">
                <div className="buhHistoryItemDescription">
                  {historyItem.fileKey}
                </div>
                <div className="buhHistoryItemCreatedAtBy">
                  {createdAtBy}
                </div>
              </div>
              <div className="buhHistoryItemCenter" />
              <div className="buhHistoryItemRight">
                {
                  isComplete
                    ? (
                      renderCompleteItem(historyItem)
                    )
                    : (
                      renderBusyItem(historyItem)
                    )
                }
              </div>
            </div>
          );
        });
      }

      return (
        <div className="buhHistoryNoItems">
          No History is available
        </div>
      );
    }

    return (
      <ContentLoading loadingMessage="Loading History ..." />
    );
  };

  // Make the API Call to get the Bulk Upload History
  const getBulkUploadHistory = () => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      const startRow = (currentPage - 1) * currentPageSize; // startRow is 0 based, CurrentPage is 1 based
      const endRow = startRow + currentPageSize; // First row not returned, endRow is 0 based

      const bulkUploadSummaryCollection: BulkUploadSummaryCollection = await api.bulkupload.getBulkUploadHistoryV4(startRow, endRow);
      const bulkUploadHistory: BulkUploadSummary[] = bulkUploadSummaryCollection.value;
      setTotalBulkUploadHistory(bulkUploadSummaryCollection['@odata.count']);

      Logging.Info('Get Bulk History', startRow, endRow, bulkUploadHistory.length);

      // Return a Sorted List of Bulk Upload History
      setHistoryList(bulkUploadHistory);
    } catch (error) {
      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Bulk Upload History Error',
        description: 'Unable to read the Bulk Upload History.',
        error: error as ResponseError,
      };
      setNotification(notificationData);
    }
  };

  // Refresh of the Bulk Upload History on an interval
  React.useEffect(() => {
    const interval = setInterval(dispatch(getBulkUploadHistory), BulkUploadHistoryRefreshIntervalSeconds * 1000);

    return (): void => {
      clearInterval(interval);
    };
  }, [historyList]);

  // Initial Load of the Bulk Upload History and when Paging Changes
  React.useEffect(() => {
    dispatch(getBulkUploadHistory());
  }, [currentPage, currentPageSize, numberUploads]);

  // Pagination parameters have changed
  const onPaginationChange = (page: number, pageSize: number): void => {
    Logging.Debug('onPaginationChange', page, pageSize);

    setCurrentPage(page);
    setCurrentPageSize(pageSize);
  };

  return (
    <>
      <div className="buContainer">
        <div className="buHeader">
          <div className="buHeaderBackToCertificates">
            <VertexLink href="#/certificates" display="Back to Certificates" />
          </div>
          <div className="buHeaderMain">
            <div className="buHeaderMainLeft buhHeaderMainLeft">
              <h2>
                <span>Bulk Upload History</span>
              </h2>
            </div>
          </div>
        </div>
        <div className="buhContentContainer">
          <div className="buhContentHistory">
            {renderHistory()}
          </div>
          <VtxPagination
            total={totalBulkUploadHistory}
            current={currentPage}
            pageSize={currentPageSize}
            onChange={onPaginationChange}
            pageSizeOptions={['25', '50', '100']}
          />
        </div>
        <Notification
          data={notification}
          onNotificationClose={onNotificationClose}
          autoClose
        />
      </div>
    </>
  );
};

export default BulkUploadHistory;
